import React, { useState } from 'react';
import { withTranslation } from 'react-i18next';
import './TrainingResultView.css'; // <-- импорт стилей
import MainNav from '../../blocks/MainNav';
import {NavLink} from 'react-router-dom';
import { Button, Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnchorCircleCheck, faArrowLeft, faCheckCircle, faSave, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { notify } from "../../blocks/Notify";

const ResultsView = ({ results = {}, questions = [], questionsCache = {}, t ,questionStartedAt=[],testStartedAt,testFinishedAt,handleSaveResult}) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const [resultIsSaved, setIsSavedResult] = useState(false);
    const totalQuestions = questions.length;

    if (totalQuestions === 0) {
        return (
            <div className="results">
                <div className="results__empty">
                    {tr('No data to display', 'Нет данных для отображения')}
                </div>
            </div>
        );
    }

    // --- подсчёт статистики и подготовка строк ---
    let correctCount = 0;
    let wrongCount = 0;
    let unansweredCount = 0;
    let totalAttempts = 0;
    let firstTryCorrectCount = 0;
    const lastAnsweredAt = Object.values(results)
        .flat()
        .reduce((max, a) => Math.max(max, a.answered_at || 0), 0);

    const totalDuration = (testFinishedAt || lastAnsweredAt) - testStartedAt;
    const averageDuration = totalQuestions > 0 ? totalDuration / totalQuestions : 0;
    const rows = questions.map((q) => {
        const questionId = q.questionID ?? q.questionId;
        const attempts = Array.isArray(results[questionId]) ? results[questionId] : [];
        const total = attempts.length;

        // ключевая логика: верным считаем только первую попытку
        const firstTryCorrect = attempts[0]?.is_right === true;

        // для информации — были ли вообще верные попытки
        const hasCorrect = attempts.some((a) => a.is_right === true);

        const cached = questionsCache[questionId];
        const questionText = cached?.question?.text || q.testTitle || questionId;

        const startedAt = questionStartedAt[questionId];
        const lastAttempt = attempts[attempts.length - 1];
        const firstAttempt = attempts[0];
        let questionDuration = 0;
        if (startedAt && lastAttempt?.answered_at) {
            questionDuration = Math.max(0, lastAttempt.answered_at - startedAt);
        } else if (firstAttempt && lastAttempt && attempts.length > 1) {
            // fallback: считаем от первой до последней попытки
            questionDuration = Math.max(0, lastAttempt.answered_at - firstAttempt.answered_at);
        }
        totalAttempts += total;
        if (total === 0) unansweredCount += 1;
        else if (firstTryCorrect) correctCount += 1;
        else wrongCount += 1;
        if (firstTryCorrect) firstTryCorrectCount += 1;

        return {
            questionId,
            questionText,
            attempts,
            total,
            firstTryCorrect,
            hasCorrect,
            questionDuration
        };
    });   
    
    const percent = Math.round((correctCount / totalQuestions) * 100);
    const percentFirstTry = Math.round((firstTryCorrectCount / totalQuestions) * 100);

    // цветовой порог для процента
    const percentModifier =
        percent >= 80 ? 'is-high' : percent >= 50 ? 'is-mid' : 'is-low';

    const getRowStatus = (row) => {
        if (row.total === 0) return tr('Skipped', 'Пропущен');
        return row.firstTryCorrect
            ? tr('Correct', 'Правильно')
            : tr('With error', 'С ошибкой');
    };
   
    const getRowModifier = (row) => {
        if (row.total === 0) return 'results__row_empty';
        return row.firstTryCorrect
            ? 'results__row_correct'
            : 'results__row_wrong';
    };
    const getStatusModifier = (row) => {
        if (row.total === 0) return 'empty';
        return row.firstTryCorrect ? 'correct' : 'wrong';
    };
    const formatDuration = (ms) => {
        if (!Number.isFinite(ms) || ms < 0) return '—';
        const totalSec = Math.floor(ms / 1000);
        const min = Math.floor(totalSec / 60);
        const sec = totalSec % 60;
        return `${min}:${String(sec).padStart(2, '0')}`;
    };

    return (
        <>
            <div className="Screen container mx-auto">
                {/* ---- Шапка со сводкой ---- */}
                <div className="results__summary">
                    <div className="results__summary-main">
                        <div className={`results__percent ${percentModifier}`}>
                            <span className="results__percent-value">{percent}%</span>
                            <span className="results__percent-label">
                                {tr('Correct answers', 'Правильных ответов')}
                            </span>
                        </div>
                        <div className="results__metrics">
                            <div className="results__metric">
                                <div className="results__metric-label">
                                    {tr('Total', 'Всего')}
                                </div>
                                <div className="results__metric-value">{totalQuestions}</div>
                            </div>
                            <div className="results__metric results__metric_correct">
                                <div className="results__metric-label">
                                    {tr('Correct', 'Верно')}
                                </div>
                                <div className="results__metric-value">{correctCount}</div>
                            </div>
                            <div className="results__metric results__metric_wrong">
                                <div className="results__metric-label">
                                    {tr('Wrong', 'Неверно')}
                                </div>
                                <div className="results__metric-value">{wrongCount}</div>
                            </div>
                            <div className="results__metric results__metric_empty">
                                <div className="results__metric-label">
                                    {tr('Skipped', 'Пропущено')}
                                </div>
                                <div className="results__metric-value">{unansweredCount}</div>
                            </div>
                        </div>
                    </div>

                    <div className="results__summary-extra">
                        <div className="results__extra-item">
                            <span className="results__extra-label">
                                {tr('Total attempts', 'Всего попыток')}
                            </span>
                            <span className="results__extra-value">{totalAttempts}</span>
                        </div>
                        <div className="results__extra-item">
                            <span className="results__extra-label">
                                {tr('From first attempt', 'С первой попытки')}
                            </span>
                            <span className="results__extra-value">{percentFirstTry}%</span>
                        </div>
                        <div className="results__extra-item">
                            <span className="results__extra-label">
                                {tr('Total time', 'Общее время')}
                            </span>
                            <span className="results__extra-value">
                                {formatDuration(totalDuration)}
                            </span>
                        </div>
                        <div className="results__extra-item">
                            <span className="results__extra-label">
                                {tr('Average per question', 'В среднем на вопрос')}
                            </span>
                            <span className="results__extra-value">
                                {formatDuration(averageDuration)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* ---- Таблица (скроллится) ---- */}
                <div className="results__table-wrap">
                    <table className="results__table">
                        <thead>
                            <tr>
                                <th className="results__col-num">#</th>
                                <th className="results__col-question">
                                    {tr('Question', 'Вопрос')}
                                </th>
                                <th className="results__col-attempts">
                                    {tr('Attempts', 'Попыток')}
                                </th>
                                <th className="results__col-time">
                                    {tr('Time', 'Время')}
                                </th>                            
                                <th className="results__col-status">
                                    {tr('Status', 'Итог')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => (
                                <tr
                                    key={row.questionId}
                                    className={`results__row ${getRowModifier(row)}`}
                                >
                                    <td className="results__col-num">{index + 1}</td>
                                    <td className="results__col-question" title={row.questionText}>
                                        {row.questionText}
                                    </td>
                                    <td className="results__col-attempts">
                                        <span className="results__badge">{row.total}</span>
                                    </td>
                                <td className="results__col-time">
                                        {row.total === 0 ? '—' : formatDuration(row.questionDuration)}
                                    </td>
                                    <td className="results__col-status">
                                        <span className={`results__status results__status_${getStatusModifier(row)}`}>
                                            {getRowStatus(row)}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <MainNav>
                <Container className={`d-flex justify-content-between`}>
                    <NavLink to={"/cabinet"} className={`Btn Btn_success MuiButton`}>
                        <FontAwesomeIcon className="mr-4" icon={faArrowLeft}/>
                        <span>{tr('Personal Area', 'В личный кабинет')}</span>
                    </NavLink>
                    <Button onClick={()=>{
                            if(resultIsSaved){
                                notify(tr('Training result is already saved'), 'success');
                            }else{
                               
                                handleSaveResult().then(()=>{
                                     notify(tr('Training result is saved'), 'success');
                                    setIsSavedResult(true);
                                }).catch(()=>{
                                    notify(tr('Training result error saved'), 'danger');
                                });
                              
                            }
                         }} className={`Btn Btn_success MuiButton`}>
                        <FontAwesomeIcon className="mr-4" icon={resultIsSaved?faCheckCircle:faSave}/>
                        <span>{tr('Save results','Сохранить результат')}</span>
                    </Button>
                    <NavLink to={"/"} className={`Btn Btn_danger MuiButton`}>
                        <FontAwesomeIcon className="mr-4" icon={faSignOutAlt}/>
                        <span>{tr('Exit','Выйти')}</span>
                    </NavLink>
                </Container>
            </MainNav>
   
        </>
    );
};

export default withTranslation()(ResultsView);